import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/context/ContentContext";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

const STYLE_OPTIONS = ["Hip-Hop", "Majorette", "Jazz", "Ballet", "Contemporary", "Lyrical", "Pom", "Tap"];

const EMPTY = {
  parent_name: "",
  parent_email: "",
  parent_phone: "",
  dancer_name: "",
  dancer_age: "",
  grade: "",
  experience_years: "",
  styles: [],
  availability: "",
  why_join: "",
  referral: "",
  medical_notes: "",
  video_url: "",
  photo_consent: false,
};

export default function Join() {
  const t = useT();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const toggleStyle = (s) => {
    setForm((prev) => ({
      ...prev,
      styles: prev.styles.includes(s)
        ? prev.styles.filter((x) => x !== s)
        : [...prev.styles, s],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        dancer_age: parseInt(form.dancer_age, 10) || 0,
        experience_years: parseInt(form.experience_years, 10) || 0,
      };
      await api.post("/tryouts", payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="mx-auto max-w-3xl w-full px-6 py-24 flex-1 text-center">
          <div className="inline-flex h-20 w-20 rounded-full bg-gradient-to-br from-[#0055FF] to-[#00E5FF] grid place-items-center mx-auto mb-6 animate-pulse-glow">
            <CheckCircle2 size={36} className="text-white" />
          </div>
          <div className="overline mb-3">Application Received</div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">
            {t("join.success.title", "We got it!")}
          </h1>
          <p className="mt-4 text-white/70 max-w-xl mx-auto leading-relaxed">
            {t("join.success.body", "")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/" className="btn-electric" data-testid="join-success-home">Back to Home</Link>
            <Link to="/gallery" className="btn-ghost" data-testid="join-success-gallery">See the Team</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="mx-auto max-w-3xl w-full px-6 py-16 flex-1">
        <div className="overline mb-3">{t("join.page.overline", "How to Join")}</div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">
          {t("join.page.title", "Tryout for the Dymes")}
        </h1>
        <p className="mt-4 text-white/65 max-w-2xl leading-relaxed">
          {t("join.page.subtitle", "")}
        </p>

        <form onSubmit={submit} className="mt-12 space-y-12" data-testid="join-form">
          {/* SECTION 1 — family */}
          <section className="card-dark p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="text-[#00E5FF]" size={16} />
              <h2 className="font-varsity text-xl tracking-wider">{t("join.step1.title", "Tell us about your family")}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Parent name *</label>
                <input type="text" required className="input-line mt-1" value={form.parent_name}
                       onChange={update("parent_name")} placeholder="Jane Doe"
                       data-testid="join-parent-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Phone *</label>
                <input type="tel" required className="input-line mt-1" value={form.parent_phone}
                       onChange={update("parent_phone")} placeholder="(555) 123-4567"
                       data-testid="join-parent-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Email *</label>
                <input type="email" required className="input-line mt-1" value={form.parent_email}
                       onChange={update("parent_email")} placeholder="you@email.com"
                       data-testid="join-parent-email" />
              </div>
            </div>
          </section>

          {/* SECTION 2 — dancer */}
          <section className="card-dark p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="text-[#00E5FF]" size={16} />
              <h2 className="font-varsity text-xl tracking-wider">{t("join.step2.title", "Tell us about your dancer")}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Dancer's name *</label>
                <input type="text" required className="input-line mt-1" value={form.dancer_name}
                       onChange={update("dancer_name")} placeholder="Aaliyah"
                       data-testid="join-dancer-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Age *</label>
                <input type="number" required min={2} max={25} className="input-line mt-1" value={form.dancer_age}
                       onChange={update("dancer_age")} placeholder="9"
                       data-testid="join-dancer-age" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">School / grade</label>
                <input type="text" className="input-line mt-1" value={form.grade}
                       onChange={update("grade")} placeholder="4th grade"
                       data-testid="join-dancer-grade" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Years of dance experience *</label>
                <input type="number" required min={0} max={30} className="input-line mt-1" value={form.experience_years}
                       onChange={update("experience_years")} placeholder="2"
                       data-testid="join-experience" />
              </div>
            </div>

            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Dance styles your dancer has done</label>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="join-styles-group">
                {STYLE_OPTIONS.map((s) => {
                  const on = form.styles.includes(s);
                  return (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleStyle(s)}
                      className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] transition-all ${
                        on
                          ? "bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-white border border-transparent"
                          : "border border-white/15 text-white/70 hover:border-[#00E5FF]/40"
                      }`}
                      data-testid={`join-style-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Days / times she's usually available</label>
              <textarea rows={2} className="input-line mt-1 resize-none" value={form.availability}
                        onChange={update("availability")} placeholder="Weekdays after 5pm, all weekend"
                        data-testid="join-availability" />
            </div>
          </section>

          {/* SECTION 3 — a few more */}
          <section className="card-dark p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="text-[#00E5FF]" size={16} />
              <h2 className="font-varsity text-xl tracking-wider">{t("join.step3.title", "A couple of last things")}</h2>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Why does she want to join Dazzlin&apos; Dymes?</label>
              <textarea rows={3} className="input-line mt-1 resize-none" value={form.why_join}
                        onChange={update("why_join")} placeholder="She lights up every time she watches the team perform…"
                        data-testid="join-why" />
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">How did you hear about us?</label>
                <input type="text" className="input-line mt-1" value={form.referral}
                       onChange={update("referral")} placeholder="Instagram, friend, etc."
                       data-testid="join-referral" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Any medical notes / allergies?</label>
                <input type="text" className="input-line mt-1" value={form.medical_notes}
                       onChange={update("medical_notes")} placeholder="Asthma, nut allergy, etc."
                       data-testid="join-medical" />
              </div>
            </div>

            <div className="mt-5">
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Showcase video (optional)</label>
              <input type="url" className="input-line mt-1" value={form.video_url}
                     onChange={update("video_url")}
                     placeholder="Paste a YouTube, Google Drive, Vimeo, or .mp4 link"
                     data-testid="join-video-url" />
              <div className="text-[11px] text-white/40 mt-2 leading-relaxed">
                Want Coach to see your dancer move before tryouts? Upload a short clip (30–60 seconds is perfect) to YouTube (Unlisted), Google Drive, or Vimeo, then paste the share link here. Make sure the link is set so we can view it without signing in.
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.photo_consent}
                     onChange={update("photo_consent")} className="mt-1"
                     data-testid="join-photo-consent" />
              <span className="text-sm text-white/75 leading-snug">
                I give permission for my dancer to appear in team photos/videos used on this website and social media.
              </span>
            </label>
          </section>

          {error && (
            <div className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 rounded px-3 py-2" data-testid="join-error">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" disabled={submitting} className="btn-electric" data-testid="join-submit-btn">
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : "Send Application"}
            </button>
            <span className="text-xs text-white/40">We reply within 48 hours.</span>
          </div>
        </form>
      </main>

      <Footer />
    </div>
  );
}
